import {Component, Inject, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Data} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {environment} from "@environment";
import {
  AbstractComponent,
} from "@components";
import {
  ApplicationFormSettings,
  CategoryItem,
  Competitor,
  CompetitorFilter,
  CompetitorResults,
  Country,
  EventItem,
  EventStatus,
  Lookups, PagedFilter,
  Race, Pages,
  RaceSettings,
  TypeOfApplicationTypes,
} from "@model";
import {CompetitorService,
  EventService, PublicEventsService,
} from "@services";
import {DOCUMENT} from "@angular/common";
import {EventDetailsHolder} from "@resolvers";
import {Meta, Title} from '@angular/platform-browser';
import {buildEventSlug, getLanguageValue, localizedDateString} from '@utility';

@Component({
    selector: 'app-event-details',
    templateUrl: './event-details.component.html',
    styleUrls: ['./event-details.component.scss'],
    standalone: false
})
export class EventDetailsComponent extends AbstractComponent implements OnInit {
  @Input() eventDetailsHolder?: EventDetailsHolder;
  @Input('eventDetails') event?: EventItem;
  @Input() countries: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() results: CompetitorResults[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input() competitors: Competitor[] = [];
  @Input() shared?: string;

  eventCountry?: Country;
  eventCurrency?: string;

  componentType: EventDetailsComponentType  = EventDetailsComponentType.DETAILS;

  lengths: (string | undefined)[] = [];
  applicationFormSettingsList: ApplicationFormSettings[] = [];
  settingsList: RaceSettings[] = [];
  hideCompetitors: boolean = true;
  competitorTableEvent?: EventItem;
  races: Race[] = [];

  constructor(
    private eventService: EventService,
    private publicEventService: PublicEventsService,
    public translocoService: TranslocoService,
    private route: ActivatedRoute,
    private competitorService: CompetitorService,
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) public document: Document,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.eventDetailsHolder) {
      this.event = this.eventDetailsHolder.eventItem;
      this.results = this.eventDetailsHolder.results;
      this.categories = this.eventDetailsHolder.categories;
      this.setSEOData();
    }

    this.subscribeToRouteDataChange();
    this.eventService.setCurrentEvent(this.event);
    this.setEventLengths();
    this.setEventCountry();
    this.setEventCurrency();
    this.applicationFormSettingsList = (this.event?.racesList?.flatMap(item => item.applicationFormSettings) ?? []).filter((item): item is ApplicationFormSettings => !!item);
    this.settingsList = (this.event?.racesList?.flatMap(item => item.raceSettings) ?? []).filter((item): item is RaceSettings => !!item);
    this.hideCompetitors = this.event?.status === EventStatus.FINISHED
      || this.competitors.length === 0
      || this.settingsList.every(setting => setting.typeOfApplication === TypeOfApplicationTypes.DO_NOT_DISPLAY_PARTICIPANT_LIST);
    if (!this.hideCompetitors) {
      this.competitorTableEvent = {...this.event};
      this.competitorTableEvent.racesList = [
        ...(this.competitorTableEvent.racesList?.filter(
          (race: Race) =>
            !race.raceSettings ||
            race.raceSettings.typeOfApplication !== TypeOfApplicationTypes.DO_NOT_DISPLAY_PARTICIPANT_LIST
        ) ?? [])
      ];
    }
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({isPreview}: Data) => {
        if (isPreview) {
          this.componentType = EventDetailsComponentType.PREVIEW;
        }
      }),
    );
  }

  setEventLengths(): void {
    if (this.event?.lengths?.length) {
      // @ts-ignore
      this.lengths = this.eventItem.lengths.sort((a, b) => (a || 0) - (b || 0))
    } else {
      this.lengths = Array.from(new Set(this.event?.racesList?.map(race => race.length).sort((a, b) => (a || 0) - (b || 0)).map(length => length?.toString())));
    }
  }




  setCompetitors() {
    if (this.event?.racesList?.at(0)) {
      const filter: CompetitorFilter = new CompetitorFilter();
      filter.eventId = this.event?.id;
      filter.raceId = this.event?.racesList?.at(0)?.id;
      this.competitorService.findCompetitors(
          new PagedFilter(new Pages(undefined, 1), filter), false, false).subscribe(value =>
      this.competitors = value
      );
    }
    else {
      this.competitors = [];
    }
  }


  private setEventCountry(): void {
    if (this.countries.length && this.event?.country) {
      this.eventCountry = this.countries.find(item => item.id === this.event?.country);
    }
  }

  private setEventCurrency(): void {
    this.eventCurrency = this.currencies.find(currency => currency.id === this.event?.currency)?.name ?? "";
  }

  formatDate(date: Date | string): string {
    return localizedDateString(date, 'MMM', this.getLocale())?.slice(0, 3)?.toUpperCase() ?? '';
  }

  getLocale() {
    let activeLang = this.translocoService.getActiveLang();
    switch (activeLang) {
      case 'hr':
        return 'hr';
      case 'en':
        return 'en';
      case 'it':
        return 'it';
      case 'slo':
        return 'slo';
      case 'sr':
        return 'sr-Latn';
      case 'bs':
        return 'sr-Latn';
      default:
        return 'en';
    }
  }

  setSEOData(): void {
    if (this.event) {
      const lang = this.translocoService.getActiveLang();

      const title = getLanguageValue(
        this.event.name, this.event.nameEng, this.event.nameIta,
        this.event.nameSlo, this.event.nameSr, this.event.nameBs, lang
      ) ?? '';

      const description = title;

      this.titleService.setTitle(title);

      let url = this.document.location.href.replace("/private", "");
      if (this.event.status === EventStatus.FINISHED) {
        url = url.replace("/profile/myTracey", "/finishedEvents");
      } else {
        url = url.replace("/profile/myTracey", "/eventCalendar");
      }

      let canonicalUrl: string;
      if (this.event.id) {
        const slug = buildEventSlug(this.event.id, this.event);
        const urlObj = new URL(url);
        const pathBase = urlObj.pathname.split('/eventDetails')[0] + '/eventDetails';
        canonicalUrl = `${urlObj.origin}${pathBase}/${slug}`;
      } else {
        canonicalUrl = url.split('?')[0];
      }
      const image = this.event.imageMedium ?? `${this.document.location.origin}/assets/img/hero2-mobile.webp`;

      this.metaService.updateTag({name: 'description', content: description});

      this.metaService.updateTag({property: 'og:url', content: canonicalUrl});
      this.metaService.updateTag({property: 'og:title', content: title});
      this.metaService.updateTag({property: 'og:type', content: 'website'});
      this.metaService.updateTag({property: 'og:site_name', content: environment.site_name});
      this.metaService.updateTag({property: 'og:description', content: description});
      this.metaService.updateTag({property: 'og:image', content: image});

      this.metaService.updateTag({name: 'twitter:card', content: 'summary_large_image'});
      this.metaService.updateTag({name: 'twitter:title', content: title});
      this.metaService.updateTag({name: 'twitter:description', content: description});
      this.metaService.updateTag({name: 'twitter:image', content: image});

      this.setCanonicalUrl(canonicalUrl);
    }
  }

  private setCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  protected readonly environment = environment;
  protected readonly EventStatus = EventStatus;
  protected readonly EventDetailsComponentType = EventDetailsComponentType;
}
export enum EventDetailsComponentType {
  DETAILS,
  PREVIEW,
}



